import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios, { AxiosError } from 'axios';
import { formatCostPatientResponse, computeDiagnosisData } from '@/lib/utils/episodesAndTreatmentSets';
import PatientDataUtil from '@/lib/utils/PatientDataUtil';
import { getReconData } from '@/lib/utils/episodesAndTreatmentSets';
import { formatRiskConditions } from '@/lib/utils/episodesAndTreatmentSets';
import _uniqBy from 'lodash/uniqBy';
import _filter from 'lodash/filter';
import { clearError } from './errorSlice';
import { handleApiError } from '@/utils/apiErrorHandler';

export const fetchAdjudication = createAsyncThunk('case/fetchAdjudication', async ({ requestID, caseID }: any, { getState }) => {
  const state = getState() as { user: { user: { username: string } } };
  const { username } = state.user.user;
  const response = await axios.post(`/api/handler`, {
    method: 'GET',
    url: `/odm/requests/adjudication/${requestID}?caseID=${caseID}`,
  }, {
    headers: {
      'X-AUTH-USERNAME': username,
    }
  });
  return response.data;
});

export const fetchCostPatientsRiskCategories = createAsyncThunk(
  'costPatient/fetchCostPatientsRiskCategories',
  async ({ mpin, page, patientID, size, treatmentSet, isDelegate }:
    { mpin: string, page: number, patientID: string, size: number, treatmentSet: string, isDelegate: boolean }, { rejectWithValue, dispatch }) => {
    let url = !isDelegate ? `/papi/secure/costPatient/cost-patients-risk-category?page=${page}&size=${size}`
      : `/papi/secure/physician/${mpin}/costPatient/cost-patients-risk-category?page=${page}&size=${size}`;
    if (treatmentSet) {
      url = `${url}&treatmentSet=${treatmentSet}`;
    }
    if (patientID) url = `${url}&patientID=${patientID}`
    try {
      dispatch(clearError());
      console.log('routeUrl cost-patients-risk-category get from:', url);
      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      //  pendingRequests.delete(requestId);
      handleApiError(error as AxiosError, "costPatient", dispatch);
      throw error;
    }
  });

export const fetchPatients = createAsyncThunk(
  'costPatient/fetchPatients',
  async ({ mpin, page, size, sort, riskCategory, treatmentSet, patientID, isDelegate }:
    {
      mpin: string;
      page: number;
      size: number;
      sort: string,
      riskCategory?: string,
      treatmentSet?: string,
      patientID?: string,
      isDelegate?: boolean
    }, { dispatch }) => {
    let url = !isDelegate ? `/papi/secure/costPatient/cost-patient-patients?page=${page}&size=${size}&sort=${sort}`
      : `/papi/secure/physician/${mpin}/costPatient/cost-patient-patients?page=${page}&size=${size}&sort=${sort}`;
    if (riskCategory) {
      url = `${url}&riskCategory=${riskCategory}`;
    }
    if (treatmentSet) {
      url = `${url}&treatmentSet=${treatmentSet}`;
    }
    if (patientID) {
      url = `${url}&patientID=${patientID}`;
    }
    try {
      dispatch(clearError());
      console.log('routeUrl cost-patient-patients get from:', url);
      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      //  pendingRequests.delete(requestId);
      handleApiError(error as AxiosError, "costPatient", dispatch);
      throw error;
    }
  });

export const fetchTreatmentSets = createAsyncThunk('costPatient/fetchTreatmentSets',
  async ({ mpin, page, size, sort, patientID, riskCategory, treatmentSet, section, reconCaseID, changeRequestApplied, isDelegate, isCaseClosed }:
    {
      mpin: string;
      page: number;
      size: number;
      sort: string,
      treatmentSet?: string,
      riskCategory?: string,
      patientID?: string,
      section?: string,
      reconCaseID?: string,
      changeRequestApplied?: string,
      isDelegate: boolean,
      isCaseClosed: boolean
    }, { dispatch }) => {
    let url = '';
    if (isCaseClosed) {
      url = !isDelegate ? `/papi/secure/case/treatment-sets?page=${page}&size=${size}&sort=${sort}` : `/papi/secure/physician/${mpin}/case/treatment-sets?page=${page}&size=${size}&sort=${sort}`;
    } else {
      url = !isDelegate ? `/papi/secure/costPatient/cost-patients?page=${page}&size=${size}&sort=${sort}` : `/papi/secure/physician/${mpin}/costPatient/cost-patients?page=${page}&size=${size}&sort=${sort}`;
    }
    if (reconCaseID) url = `${url}&reconCaseID=${reconCaseID}`
    if (riskCategory) {
      url = `${url}&riskCategory=${riskCategory}`;
    }
    if (treatmentSet) {
      url = `${url}&treatmentSet=${treatmentSet}`;
    }
    if (patientID) {
      url = `${url}&reconPatientPatientID=${patientID}`;
    }
    if (section) url = `${url}&section=${section}`;
    if (changeRequestApplied) url = `${url}&changeRequestApplied=${changeRequestApplied}`;
    try {
      dispatch(clearError());
      console.log('routeUrl cost-patients get from:', url);
      const response = await axios.get(url);
      console.log('Response from fetchTreatmentSets:', response);
      return {
        data: response?.data?.data,
        totalItems: response?.data?.totalItems,
        variant: size === 10 ? 'table' : 'dropdown',
      };
    } catch (error) {
      handleApiError(error as AxiosError, "costPatient", dispatch);
      throw error;
    }
  });

export const patientExclusionGet = createAsyncThunk(
  'costPatient/patientExclusionGet', async ({ mpin, size, sort, treatmentSetID, inCaseDetails, isDelegate, reconCaseID }:
    { mpin: string, size: number, treatmentSetID: string, sort: string, inCaseDetails?: boolean, isDelegate: boolean, reconCaseID?: string }, { rejectWithValue, dispatch }) => {
  let url = !isDelegate ? `/papi/secure/costPatient/cost-patient-exclusion?size=${size}&treatmentSetID=${treatmentSetID}&sort=${sort}` : `/papi/secure/physician/${mpin}/costPatient/cost-patient-exclusion?size=${size}&treatmentSetID=${treatmentSetID}&sort=${sort}`;
  if (reconCaseID) url += `&reconCaseSystemID=${reconCaseID}`;
  try {
    dispatch(clearError());
    console.log('routeUrl cost-patients-risk-category get from:', url);
    const response = await axios.get(url);
    return { ...response.data, inCaseDetails };
  } catch (error) {
    handleApiError(error as AxiosError, "costPatient", dispatch);
    throw error;
  }
});

export const patientExclusionPost = createAsyncThunk(
  'costPatient/patientExclusionPost', async ({ mpin, payload, update, isDelegate }:
    { mpin: string, payload: any, update: boolean, isDelegate: boolean }, { dispatch }) => {
  let url = !isDelegate ? `/papi/secure/costPatient/cost-patient-exclusion` : `/papi/secure/physician/${mpin}/costPatient/cost-patient-exclusion`;
  if (update) {
    url += `?update=${update}`;
  }
  try {
    dispatch(clearError());
    console.log('routeUrl cost-patients-risk-category get from:', url);
    const response = await axios.post(url, payload);
    return response.status;
  } catch (error) {
    handleApiError(error as AxiosError, "costPatient", dispatch);
    throw error;
  }
});

export const patientExclusionDelete = createAsyncThunk(
  'costPatient/patientExclusionDelete', async ({ mpin, treatmentSetID, isDelegate }:
    { mpin: string, treatmentSetID: string, isDelegate: boolean }, { rejectWithValue, dispatch }) => {
  const url = !isDelegate ? `/papi/secure/costPatient/cost-patient-exclusion/remove` :
    `/papi/secure/physician/${mpin}/costPatient/cost-patient-exclusion/remove`;
  try {
    dispatch(clearError());
    console.log('routeUrl cost-patient-exclusion remove from:', url);
    const response = await axios.post(url, { treatmentSetID });
    return response.status;
  } catch (error) {
    handleApiError(error as AxiosError, "costPatient", dispatch);
    throw error;
  }
});

export const riskExclusionConditions = createAsyncThunk('costPatient/riskExclusionConditions',
  async ({ mpin, size, treatmentSetID, isDelegate }: { mpin: string, size: number, treatmentSetID: string, isDelegate: boolean }, { rejectWithValue, dispatch }) => {
    let url = !isDelegate ? `/papi/secure/costPatient/cost-patients-risk-exclusion?size=${size}&treatmentSetID=${treatmentSetID}` :
      `/papi/secure/physician/${mpin}/costPatient/cost-patients-risk-exclusion?size=${size}&treatmentSetID=${treatmentSetID}`;
    try {
      dispatch(clearError());
      console.log('routeUrl cost-patients-risk-exclusion get from:', url);
      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError, "costPatient", dispatch);
      throw error;
    }
  });

export const riskExclusionPatientPost = createAsyncThunk(
  'costPatient/riskExclusionPatientPost', async ({ mpin, payload, update, isDelegate }:
    { mpin: string, payload: any, update: boolean, isDelegate: boolean }, { rejectWithValue, dispatch }) => {
  let url = !isDelegate ? `/papi/secure/costPatient/cost-patients-risk-exclusion` :
    `/papi/secure/physician/${mpin}/costPatient/cost-patients-risk-exclusion`;
  if (update) {
    url += `?update=${update}`;
  }
  try {
    dispatch(clearError());
    console.log('routeUrl cost-patients-risk-exclusion post from:', url);
    const response = await axios.post(url, payload);
    return response.data;
  } catch (error) {
    handleApiError(error as AxiosError, "costPatient", dispatch);
    throw error;
  }
});

export const riskExclusionPatientDelete = createAsyncThunk(
  'costPatient/riskExclusionDelete',
  async (
    { mpin, treatmentSetID, conditions, isDelegate }:
      { mpin: string, treatmentSetID: string, conditions: Array<any>, isDelegate: boolean },
    { rejectWithValue, dispatch }
  ) => {
    const url = !isDelegate
      ? `/papi/secure/costPatient/cost-patients-risk-exclusion/remove`
      : `/papi/secure/physician/${mpin}/costPatient/cost-patients-risk-exclusion/remove`;

    try {
      dispatch(clearError());
      console.log('routeUrl cost-patients-risk-exclusion remove from:', url);
      const response = await axios.post(url, { treatmentSetID, conditions });
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError, "costPatient", dispatch);
      throw error;
    }
  }
);

export const fetchPatientAttributionService = createAsyncThunk(
  'costPatient/fetchPatientAttributionService', async ({ mpin, page, size, sort, treatmentSetID, specialCategorySystemID, isDelegate }:
    { mpin: string; page: number; size: number; sort: string, treatmentSetID: string, specialCategorySystemID: string, isDelegate: boolean }, { rejectWithValue, dispatch }) => {
  let url = !isDelegate
    ? `/papi/secure/costPatient/cost-patients-service-attribution?page=${page}&size=${size}&sort=${sort}&treatmentSetID=${treatmentSetID}&specialCategorySystemID=${specialCategorySystemID}`
    : `/papi/secure/physician/${mpin}/costPatient/cost-patients-service-attribution?page=${page}&size=${size}&sort=${sort}&treatmentSetID=${treatmentSetID}&specialCategorySystemID=${specialCategorySystemID}`;
  try {
    dispatch(clearError());
    console.log('routeUrl cost-patients-service-attributionn post from:', url);
    const response = await axios.get(url);
    return { data: response?.data?.data, totalItems: response?.data?.totalItems };
  } catch (error) {
    handleApiError(error as AxiosError, "costPatient", dispatch);
    throw error;
  }
});

export const fetchPatientServiceDetails = createAsyncThunk(
  'costPatient/fetchPatientServiceDetails', async ({ mpin, page, size, sort, direction, treatmentSetID, specialCategorySystemID, isDelegate }:
    { mpin: string; page: number; size: number; sort: string, direction: string, treatmentSetID: string, specialCategorySystemID: string, isDelegate: boolean }, { rejectWithValue, dispatch }) => {
  let url = !isDelegate ? `/papi/secure/costPatient/cost-patients-service-details?page=${page}&size=${size}&sort=${sort},${direction}&treatmentSetID=${treatmentSetID}&specialCategorySystemID=${specialCategorySystemID}`
    : `/papi/secure/physician/${mpin}/costPatient/cost-patients-service-details?page=${page}&size=${size}&sort=${sort},${direction}&treatmentSetID=${treatmentSetID}&specialCategorySystemID=${specialCategorySystemID}`;
  try {
    dispatch(clearError());
    console.log('routeUrl cost-patients-service-details post from:', url);
    const response = await axios.get(url);
    console.log('response in fetchPatientServiceDetails:', response);
    return { data: response?.data?.data, totalItems: response?.data?.totalItems };
  } catch (error) {
    handleApiError(error as AxiosError, "costPatient", dispatch);
    throw error;
  }
});

export const patientServiceDetailExlusionPost = createAsyncThunk(
  'case/patientServiceDetailExlusionPost', async ({ mpin, payload, update, isDelegate }:
    { mpin: string; payload: any, update: boolean, isDelegate: boolean }, { rejectWithValue, dispatch }) => {
  let url = !isDelegate ? `/papi/secure/costPatient/cost-patients-service-details-exclusion`
    : `/papi/secure/physician/${mpin}/costPatient/cost-patients-service-details-exclusion`;
  if (update) {
    url += `?update=${update}`;
  }
  try {
    dispatch(clearError());
    console.log('routeUrl cost-patients-service-details-exclusion post from:', url);
    const response = await axios.post(url, payload);
    console.log('response in patientServiceDetailExlusionPost:', response);
    return response.status
  } catch (error) {
    handleApiError(error as AxiosError, "costPatient", dispatch);
    throw error;
  }
});

export const patientServiceDetailExlusionDelete = createAsyncThunk(
  'case/patientServiceDetailExlusionDelete',
  async ({ mpin, treatmentSetID, ETGFactSystemID, isDelegate }: { mpin: string; treatmentSetID: string, ETGFactSystemID: string, isDelegate: boolean }, { rejectWithValue, dispatch }) => {
    const url = !isDelegate ? `/papi/secure/costPatient/cost-patients-service-details-exclusion/remove`
      : `/papi/secure/physician/${mpin}/costPatient/cost-patients-service-details-exclusion/remove`;
    try {
      dispatch(clearError());
      console.log('routeUrl cost-patients-service-details-exclusion remove from:', url);
      const response = await axios.post(url, { treatmentSetID, ETGFactSystemID });
      console.log('response in patientServiceDetailExlusionPost:', response);
      return response.status
    } catch (error) {
      handleApiError(error as AxiosError, "costPatient", dispatch);
      throw error;
    }
  });

export const fetchPatientServiceDetailsExclusion = createAsyncThunk(
  'case/fetchPatientServiceDetailsExclusion', async ({ mpin, page, size, sort, treatmentSetID, reconFlag, reconCaseID, specialCategorySystemID, isDelegate }:
    {
      mpin: string;
      page: number;
      size: number;
      sort: string,
      treatmentSetID: string,
      reconFlag?: number,
      reconCaseID?: string,
      specialCategorySystemID: string,
      isDelegate: boolean
    }, { dispatch }
  ) => {
  const base = !isDelegate
    ? `/papi/secure/costPatient/cost-patients-service-details?page=${page}&size=${size}&sort=${sort}&treatmentSetID=${treatmentSetID}&specialCategorySystemID=${specialCategorySystemID}`
    : `/papi/secure/physician/${mpin}/costPatient/cost-patients-service-details?page=${page}&size=${size}&sort=${sort}&treatmentSetID=${treatmentSetID}&specialCategorySystemID=${specialCategorySystemID}`;
  const url = reconCaseID
    ? `${base}&reconCaseID=${reconCaseID}`
    : reconFlag !== undefined
      ? `${base}&reconFlag=${reconFlag}`
      : base;
  try {
    dispatch(clearError());
    console.log('routeUrl cost-patients-service-details post from:', url);
    const response = await axios.get(url);
    console.log('response in patientServiceDetailExlusionPost:', response);
    return { data: response.data.data, totalItems: response.data.totalItems };
  } catch (error) {
    handleApiError(error as AxiosError, "costPatient", dispatch);
    throw error;
  }
});

export const postPatientReconAttachment = createAsyncThunk(
  'case/postPatientReconAttachment',
  async ({ mpin, payload, isDelegate }: { mpin: string; payload: any, isDelegate: boolean }, { rejectWithValue, dispatch }) => {
    let url = !isDelegate ? `/papi/secure/cost-patient/recon/attachment`
      : `/papi/secure/physician/${mpin}/cost-patient/recon/attachment`;
    try {
      dispatch(clearError());
      console.log('routeUrl cost-patient/recon/attachment post from:', url);
      const response = await axios.post(url, payload);
      console.log('response in postPatientReconAttachment:', response);
      return response.status
    } catch (error) {
      handleApiError(error as AxiosError, "costPatient", dispatch);
      throw error;
    }
  });

export const deletePatientReconAttachment = createAsyncThunk(
  'case/deletePatientReconAttachment',
  async ({ mpin, treatmentSetID, isDelegate }:
    { mpin: string; treatmentSetID: string, isDelegate: boolean }, { dispatch }) => {
    const url = !isDelegate ? `/papi/secure/cost-patient/recon/attachment/remove`
      : `/papi/secure/physician/${mpin}/cost-patient/recon/attachment/remove`;
    try {
      dispatch(clearError());
      console.log('routeUrl cost-patient/recon/attachment remove from:', url);
      const response = await axios.post(url, { treatmentSetID });
      console.log('response in deletePatientReconAttachment:', response);
      return response.status
    } catch (error) {
      handleApiError(error as AxiosError, "costPatient", dispatch);
      throw error;
    }
  });

// Create slice
const costPatientSlice = createSlice({
  name: 'costPatient',
  initialState: {
    adjudication: {
      decision: '',
      informationConsidered: ''
    },
    caseData: [],
    data: [] as any[],
    loading: false,
    totalItems: 0,
    error: null,
    riskCategories: { loading: false, data: [] },
    patients: { loading: false, data: [], totalItems: 0 },
    attribution: {
      description: '',
      attributions: [],
      loading: false,
      totalItems: 0
    },

    serviceDetails: {
      services: [] as any[],
      loading: false,
      serviceDetailsRow: {},
      totalItems: 0
    },
    riskConditions: {
      conditions: {},
      loading: false
    },
    riskDetails: {
      exclusion: {}, // Explicitly define the type
      loading: false
    },
    exclusionServiceDetails: {
      serviceExlusions: [],
      serviceExclusionsInfos: [],
      loading: false,
      totalItems: 0,
    },
    reconModalData: {},
    reconRemovedByUser: false
  },
  reducers: {
    setReconModalData: (state, action) => {
      state.reconModalData = action.payload;
    },
    setServiceDetailsRowPatient: (state, action) => {
      state.serviceDetails.serviceDetailsRow = action.payload;
    },
    setPatientServiceDetails: (state, action) => {
      const findIndex = state.serviceDetails.services.findIndex((item: any) => item.systemID === action.payload.systemID);
      if (findIndex !== -1) {
        state.serviceDetails.services[findIndex] = {
          ...state.serviceDetails.services[findIndex],
          ...action.payload
        };
      }
    },

    setTreatMentSetDetails: (state, action) => {
      const findIndex = state.data.findIndex((item: any) => String(item.treatmentSetID) === String(action.payload.treatmentSetID));
      if (findIndex !== -1) {
        state.data[findIndex] = {
          ...state.data[findIndex],
          ...action.payload
        };
      }
    },

    setReconRemovedByUser: (state, action) => {
      state.reconRemovedByUser = action.payload;
    },

    clearReconModalData: (state) => {
      state.reconModalData = {};
    },
    clearCostPatientInfo: (state) => {
      state.totalItems = 0;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCostPatientsRiskCategories.pending, (state) => {
        state.riskCategories.loading = true;
        state.error = null;
        state.riskCategories.data = [];
      })
      .addCase(fetchCostPatientsRiskCategories.fulfilled, (state, action) => {
        state.riskCategories.loading = false;
        state.riskCategories.data = action.payload?.data;
      })
      .addCase(fetchCostPatientsRiskCategories.rejected, (state, action) => {
        state.riskCategories.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchPatients.pending, (state) => {
        state.patients.loading = true;
        state.patients.data = [];
      })
      .addCase(fetchPatients.fulfilled, (state, action) => {
        state.patients.loading = false;
        state.patients.data = action.payload.data;
        state.patients.totalItems = action.payload.totalItems;
      })
      .addCase(fetchPatients.rejected, (state, action) => {
        state.patients.loading = false;
        state.patients.data = [];
        state.error = action.error.message;
      })
      .addCase(fetchTreatmentSets.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        const { size } = action.meta.arg;
        // You can derive type from size if needed
        const type = size === 10 ? 'table' : 'dropdown';

        // Example usage:
        if (type === 'dropdown') {
          state.patients.data = [];
        } else {
          state.data = [];
        }
      })
      .addCase(fetchTreatmentSets.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.variant === 'dropdown') {
          state.patients.data = action.payload.data;
          state.patients.totalItems = action.payload.totalItems;
        } else {
          state.data = formatCostPatientResponse(action.payload.data);
          state.totalItems = action.payload.totalItems;
        }

      })
      .addCase(fetchTreatmentSets.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(patientExclusionGet.pending, (state) => {
        state.riskDetails.loading = true;
        state.error = null;
        state.riskDetails.exclusion = {};
        state.exclusionServiceDetails.serviceExclusionsInfos = [];
      })
      .addCase(patientExclusionGet.fulfilled, (state, action) => {
        const fields = {
          selectedIDFieldName: 'selectedTreatmentSetID',
          selectedID: action.payload.treatmentSetID,
          reasonDescriptionName: 'reconPatientExclusionReasonDescription',
          svcDetailsIDField: 'etgfactSystemID',
          svcDetailsDescField: 'reconPatientServiceExclusionReasonDescription',
          severityOrRiskDescriptionField: 'reconPatientRiskExclusionReasonDescription',
          severityOrRiskStateVariableName: 'riskExclusions'
        };
        state.riskDetails.loading = false;
        const data = action.payload.data;
        state.exclusionServiceDetails.serviceExclusionsInfos = data;
        const uniqueCaseIDs = _uniqBy(data, 'reconCaseSystemID');
        if (!Array.isArray(data) || data.length === 0) {
          state.riskDetails.exclusion = {};
        } else {
          for (const caseId of uniqueCaseIDs) {
            const caseArray = _filter(
              data,
              recon => recon.reconCaseSystemID === (caseId as any).reconCaseSystemID
            );
            const exclusionDataForCase = getReconData(caseArray, fields);
            state.riskDetails.exclusion = exclusionDataForCase;
          }
        }
      }
      )
      .addCase(patientExclusionGet.rejected, (state, action) => {
        state.riskDetails.loading = false;
        state.error = action.error.message;
        state.exclusionServiceDetails.serviceExclusionsInfos = [];
      })
      .addCase(riskExclusionConditions.pending, (state) => {
        state.riskConditions.loading = true;
        state.error = null;
        state.riskConditions.conditions = {}
      }
      )
      .addCase(riskExclusionConditions.fulfilled, (state, action) => {
        const conditions = formatRiskConditions(action.payload?.data);
        state.riskConditions.loading = false;
        state.riskConditions.conditions = { ...conditions };
      }
      )
      .addCase(riskExclusionConditions.rejected, (state, action) => {
        state.riskConditions.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchPatientAttributionService.pending, (state) => {
        state.attribution.loading = true;
        state.error = null;
        state.attribution.attributions = [];
        state.attribution.description = '';
      })
      .addCase(fetchPatientAttributionService.fulfilled, (state, action) => {
        const service = action.payload?.data?.service?.map((item: any) => {
          return {
            ...item,
            diagnosesData: computeDiagnosisData(item),
          };
        });
        state.attribution.loading = false;
        state.attribution.attributions = service;
        state.attribution.description = action.payload?.data?.attributed?.description;
        state.attribution.totalItems = action.payload.totalItems;
      })
      .addCase(fetchPatientAttributionService.rejected, (state, action) => {
        state.attribution.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchPatientServiceDetails.pending, (state) => {
        state.serviceDetails.loading = true;
        state.error = null;
        state.serviceDetails.services = [];
      }
      )
      .addCase(fetchPatientServiceDetails.fulfilled, (state, action) => {
        const service = action.payload?.data?.map((item: any) => {
          return {
            ...item,
            diagnosesData: computeDiagnosisData(item),
            providerFullName: `${item?.providerFirstName} ${item?.providerLastName}`,
          };
        });
        state.serviceDetails.loading = false;
        state.serviceDetails.services = service;
        state.serviceDetails.totalItems = action.payload.totalItems;
      }
      )
      .addCase(fetchPatientServiceDetails.rejected, (state, action) => {
        state.serviceDetails.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchPatientServiceDetailsExclusion.pending, (state) => {
        state.exclusionServiceDetails.loading = true;
        state.error = null;
        state.exclusionServiceDetails.serviceExlusions = [];
      }
      )
      .addCase(fetchPatientServiceDetailsExclusion.fulfilled, (state, action) => {
        const service = action.payload?.data?.map((item: any) => {
          return {
            ...item,
            diagnosesData: computeDiagnosisData(item),
            providerFullName: `${item?.providerFirstName} ${item?.providerLastName}`,
          };
        });
        state.exclusionServiceDetails.loading = false;
        state.exclusionServiceDetails.serviceExlusions = service;
        state.exclusionServiceDetails.totalItems = action.payload.totalItems;
      }
      )
      .addCase(fetchPatientServiceDetailsExclusion.rejected, (state, action) => {
        state.exclusionServiceDetails.loading = false;
        state.error = action.error.message;
      }
      )
  },
});

export const {
  setPatientServiceDetails,
  setServiceDetailsRowPatient,
  setReconModalData,
  clearReconModalData,
  setTreatMentSetDetails,
  clearCostPatientInfo,
  setReconRemovedByUser
} = costPatientSlice.actions;

export default costPatientSlice.reducer;
